import { ReactNode } from 'react';
import { Navigate, Routes } from 'react-router-dom';

import {
  Typography,
  ConfirmDialog,
  useAppLayoutContextConsumer,
  Page,
} from '@occc-app/design-system';
import { SentryRoute as Route } from '@occc-app/App/sentry';
import {
  ConfigurationLayout,
  CampaignComponentTabs,
} from '@occc-app/routes/CampaignComponents/components/ConfigurationLayout';

import DesignForm from '../DesignForm';
import TargetForm from '../TargetForm';
import ScheduleForm from '../ScheduleForm';
import { GetTargetTabProps, TargetTabProps } from '../TargetForm/types';
import { useGetFormContainerProps } from '../hooks/useGetFormContainerProps';

export type OfferConfigurePageProps = ReturnType<typeof useGetFormContainerProps> & {
  dialogProps: {
    open: boolean;
    closeModal: () => void;
    offerTypeTitle: string;
    onConfirmClick: () => void;
  };
  configureForm: ReactNode;
  pageTitle: string;
  subtitle?: string;
  isDesignTabHidden: boolean;
  targetTabProps?: TargetTabProps | GetTargetTabProps;
  showExternalUrl: boolean;
  showSurgeonGeneralsWarning: boolean;
};

export const OfferConfigurePage = ({
  layoutProps,
  formContainersProps,
  scheduleFormProps,
  dialogProps,
  configureForm,
  targetTabProps,
  pageTitle,
  subtitle,
  isDesignTabHidden,
  showExternalUrl,
  showSurgeonGeneralsWarning,
}: OfferConfigurePageProps) => {
  useAppLayoutContextConsumer(layoutProps.breadcrumbLinks);
  const resolvedTargetTabProps =
    typeof targetTabProps === 'function'
      ? targetTabProps(formContainersProps.offer)
      : targetTabProps;

  return (
    <Page
      browserTabTitle={pageTitle}
      containerProps={{ variant: 'md' }}
      pageHeaderProps={{ title: layoutProps.title, subtitle }}
    >
      <ConfigurationLayout
        {...layoutProps}
        isDesignTabHidden={isDesignTabHidden}
        onGetTabLabel={(item) =>
          item.value === CampaignComponentTabs.Target && resolvedTargetTabProps?.tabLabel
            ? resolvedTargetTabProps.tabLabel
            : item.label
        }
      >
        <Routes>
          <Route path='' element={<Navigate to={CampaignComponentTabs.Configure} replace />} />
          <Route path={CampaignComponentTabs.Configure} element={configureForm} />

          <Route
            path={CampaignComponentTabs.Target}
            element={
              <TargetForm
                {...formContainersProps}
                isDesignTabHidden={isDesignTabHidden}
                targetTabProps={targetTabProps}
              />
            }
          />

          {!isDesignTabHidden && (
            <Route
              path={CampaignComponentTabs.Design}
              element={
                <DesignForm
                  {...formContainersProps}
                  showExternalUrl={showExternalUrl}
                  showSurgeonGeneralsWarning={showSurgeonGeneralsWarning}
                />
              }
            />
          )}

          <Route
            path={CampaignComponentTabs.Schedule}
            element={<ScheduleForm {...scheduleFormProps} />}
          />
        </Routes>
      </ConfigurationLayout>
      <ConfirmDialog
        dialogTitle={`Cancel ${dialogProps.offerTypeTitle}`}
        open={dialogProps.open}
        confirmButtonText='Yes'
        confirmButtonProps={{
          onClick: dialogProps.onConfirmClick,
        }}
        onClose={dialogProps.closeModal}
      >
        <Typography>
          {`Wait! If you continue, this ${dialogProps.offerTypeTitle} will be deleted and your progress will be lost. Do you still want to cancel?`}
        </Typography>
      </ConfirmDialog>
    </Page>
  );
};
